import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown, AlertCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMyPendingDrafts } from '@/hooks/useCatalogEditDraft.ts';

const REFETCH_INTERVAL_MS = 15_000;

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'ahora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

// Extract a human-friendly identifier from the draft source bundle.
// Tolerant to old PascalCase persistence (Slug/Label) — same logic as the diff viewer.
function extractIdentity(sourceContentJson: string | null | undefined): { slug: string; label: string } {
  if (!sourceContentJson) return { slug: '', label: '' };
  try {
    const raw = JSON.parse(sourceContentJson) as Record<string, unknown>;
    const get = (...keys: string[]): string => {
      for (const k of keys) {
        const v = raw[k];
        if (typeof v === 'string' && v.trim()) return v;
      }
      return '';
    };
    return { slug: get('slug', 'Slug'), label: get('label', 'Label') };
  } catch {
    return { slug: '', label: '' };
  }
}

export default function CatalogPendingDraftsBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { drafts, refetch } = useMyPendingDrafts();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const reviewable = useMemo(
    () => drafts.filter((d) => d.status === 'Translated' || d.status === 'Failed'),
    [drafts],
  );

  // Auto-refetch periódico para captar drafts que terminan de traducir mientras
  // el SU está en la página. Sin esto, el banner se queda en el estado inicial.
  useEffect(() => {
    const id = setInterval(() => { refetch(); }, REFETCH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refetch]);

  // Click outside cierra el dropdown.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (reviewable.length === 0) return null;

  const handlePick = (id: string) => {
    setOpen(false);
    navigate(`/superuser/catalog/edits/${id}`);
  };

  return (
    <div ref={ref} className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 transition-colors hover:bg-amber-500/15 dark:text-amber-300"
      >
        <span className="flex items-center gap-2">
          <Bell className="size-4 shrink-0" />
          {t('superuser.catalog.banner.pendingReview', {
            count: reviewable.length,
            defaultValue: 'Tienes {{count}} cambios pendientes de revisar',
          })}
        </span>
        <span className="flex items-center gap-1 font-medium">
          {t('superuser.catalog.banner.viewAll', { defaultValue: 'Revisar' })}
          <ChevronDown className={`size-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="mt-2 max-h-80 overflow-y-auto rounded-lg border border-border bg-card shadow-sm">
          <ul className="divide-y divide-border">
            {reviewable.map((d) => {
              const failed = d.status === 'Failed';
              const Icon = failed ? AlertCircle : Sparkles;
              const identity = extractIdentity(d.sourceContentJson);
              const displayName = identity.label || identity.slug;
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(d.id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted/60"
                  >
                    <Icon className={`mt-0.5 size-4 shrink-0 ${failed ? 'text-destructive' : 'text-primary'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground">
                          {t(`superuser.catalog.tabs.${d.kind}`, { defaultValue: d.kind })}
                        </span>
                        <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground">
                          {d.operation}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium ${
                            failed
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-primary/10 text-primary'
                          }`}
                        >
                          {d.status}
                        </span>
                        <span className="ml-auto text-muted-foreground">{timeAgo(d.createdAt)}</span>
                      </div>
                      {displayName && (
                        <div className="mt-1 truncate text-sm font-medium text-foreground">
                          {displayName}
                          {identity.slug && identity.slug !== displayName && (
                            <span className="ml-2 font-mono text-xs text-muted-foreground">{identity.slug}</span>
                          )}
                        </div>
                      )}
                      {d.errorMessage && (
                        <div className="mt-1 truncate font-mono text-xs text-destructive">
                          {d.errorMessage}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
